import { convertValueAndUnit } from 'src/helpers/utilities';
import type { ConsumptionStores } from './consumption_helpers';
import type { ChartStatisticInterface } from 'src/types/Chart';
import type { Device } from 'src/types/Device';
import type { EnergyMonitoringPeriodId } from './period_helpers';
import type { TariffSummary } from './energy_tariff_helpers';
import { getCurrentTariffDetails } from './energy_tariff_helpers';
import { isEnergyLivePeriod } from './period_helpers';
import { isEnergyMonitoringDemoMode } from './energy_data_mode';
import { DEMO_KPI_CARDS } from './energy_demo_data';
import { buildLiveEnergySnapshot } from './live_energy_snapshot';
import { STANDBY_POWER_THRESHOLD_W } from './energy_insight_metrics';

export type EnergyKpiCard = {
	id: string;
	labelKey?: string;
	icon: string;
	value: string;
	infoText: string;
	color: string;
	detail: string;
	helper: string;
	statusLabel: string;
	statusLabelKey?: string;
	statusValue: string;
	progress: number;
	progressType: 'success' | 'warning' | 'danger' | 'primary';
	showStatus: boolean;
};

export const buildEnergyKpis = (
	stores: ConsumptionStores,
	devices: Record<string, Device>,
	currency: string,
	statistic: ChartStatisticInterface | null = null,
	periodId: EnergyMonitoringPeriodId = 'live',
	tariffSummary: TariffSummary | null = null,
	useDemoData = isEnergyMonitoringDemoMode(),
): EnergyKpiCard[] => {
	const live = buildLiveEnergySnapshot(stores, devices);
	const loadW = live.loadW;
	const solarW = live.solarW;
	const gridW = live.gridImportW;
	const standbyW = live.standbyW;
	const topConsumer = live.topConsumer;
	const hasPvSetup = live.source === 'pv_setup';

	if (useDemoData) {
		return DEMO_KPI_CARDS.map((card) =>
			card.id === 'tariff' && currency
				? {
						...card,
						value: card.value.replace('EUR', currency),
						detail: card.detail.replace('EUR', currency),
					}
				: card,
		);
	}

	const fmt = (w: number) => {
		if (w <= 0) return '—';
		const { value, unit } = convertValueAndUnit(w, 'W');
		return `${value} ${unit}`;
	};

	const loadProgress = loadW > 0 ? Math.min(Math.round((loadW / 7800) * 100), 100) : 0;
	const solarProgress = solarW > 0 ? Math.min(Math.round((solarW / 3200) * 100), 100) : 0;
	const measuredSupplyW = solarW + gridW;
	const selfPoweredPct = live.selfPoweredPct;

	const tariffCost = tariffSummary?.totalCost ?? null;
	const equalRateSchedule = Boolean(tariffSummary?.hasTariffs && !tariffSummary.hasDistinctRates);
	const offPeakLabel = equalRateSchedule ? 'Equal rate' : (tariffSummary?.offPeakLabel ?? '—');
	const offPeakProgress = tariffSummary?.offPeakPercent ?? 0;
	const currentTariff = getCurrentTariffDetails(currency);
	const periodLabel = isEnergyLivePeriod(periodId) ? 'Today' : 'Selected period';
	const standbyProgress = loadW > 0 ? Math.min(Math.round((standbyW / loadW) * 100), 100) : 0;

	return [
		{
			id: 'load',
			icon: 'fas fa-bolt',
			value: fmt(loadW),
			infoText: 'Live load',
			color: '--color-chart-consumed-energy',
			detail: loadW > 0 ? 'Live consumption' : 'No activity to display',
			helper: gridW > 0 ? `${fmt(gridW)} from grid` : '',
			statusLabel: 'Peak load',
			statusValue: fmt(Math.max(loadW, gridW)),
			progress: loadProgress,
			progressType: loadProgress > 70 ? 'warning' : 'primary',
			showStatus: loadW > 0,
		},
		{
			id: 'solar',
			icon: 'fas fa-solar-panel',
			value: fmt(solarW),
			infoText: 'Solar now',
			color: '--color-chart-generated-energy',
			detail:
				solarW > 0 ? 'Active generation' : hasPvSetup ? 'No solar activity' : 'PV setup needed',
			helper:
				solarW > 0
					? 'Local production'
					: hasPvSetup
						? 'No generation right now'
						: 'Set up grid and solar meters',
			statusLabel: 'Self use',
			statusValue: solarW > 0 ? `${selfPoweredPct}%` : '—',
			progress: solarProgress,
			progressType: 'success',
			showStatus: solarW > 0,
		},
		{
			id: 'selfPowered',
			icon: 'fas fa-house',
			value: measuredSupplyW > 0 ? `${selfPoweredPct}%` : '—',
			infoText: 'Self-powered',
			color: '--color-success',
			detail:
				solarW > 0
					? 'Current demand covered by solar'
					: hasPvSetup
						? 'No solar activity'
						: 'PV setup needed',
			helper: 'Based on measured solar and grid supply',
			statusLabel: 'Grid exposure',
			statusValue: measuredSupplyW > 0 ? `${100 - selfPoweredPct}%` : '—',
			progress: selfPoweredPct,
			progressType: 'success',
			showStatus: measuredSupplyW > 0,
		},
		{
			id: 'gridImport',
			icon: 'fas fa-plug',
			value: fmt(gridW),
			infoText: 'Grid import',
			color: '--color-primary',
			detail: gridW > 0 ? 'Importing from grid' : 'No grid import',
			helper: 'Export readings are excluded',
			statusLabel: 'Grid share',
			statusValue: measuredSupplyW > 0 ? `${100 - selfPoweredPct}%` : '—',
			progress: measuredSupplyW > 0 ? 100 - selfPoweredPct : 0,
			progressType: gridW > solarW ? 'warning' : 'primary',
			showStatus: measuredSupplyW > 0,
		},
		{
			id: 'periodConsumption',
			icon: 'fas fa-chart-column',
			value: statistic?.totalConsumed ?? '—',
			infoText: 'Period consumption',
			color: '--color-chart-consumed-energy',
			detail: `${periodLabel} consumption`,
			helper: statistic?.average ? `Average ${statistic.average}` : 'No statistics available',
			statusLabel: 'Previous period',
			statusValue:
				typeof statistic?.comparisonDiff === 'number' ? `${statistic.comparisonDiff}%` : '—',
			progress: 0,
			progressType: 'primary',
			showStatus: Boolean(statistic?.totalConsumed),
		},
		{
			id: 'periodGeneration',
			icon: 'fas fa-sun',
			value: statistic?.totalGenerated ?? '—',
			infoText: 'Period generation',
			color: '--color-chart-generated-energy',
			detail: `${periodLabel} solar generation`,
			helper: statistic?.totalGenerated ? 'Measured local production' : 'No generation statistics',
			statusLabel: 'Previous period',
			statusValue:
				typeof statistic?.prevTotalGeneratedValue === 'number'
					? String(statistic.prevTotalGeneratedValue)
					: '—',
			progress: 0,
			progressType: 'success',
			showStatus: Boolean(statistic?.totalGenerated),
		},
		{
			id: 'tariff',
			labelKey: isEnergyLivePeriod(periodId)
				? 'energy_monitoring_kpi_tariff'
				: 'energy_monitoring_period_cost',
			icon: 'fas fa-coins',
			value: tariffCost ?? '—',
			infoText: isEnergyLivePeriod(periodId) ? 'Today' : 'Reported cost',
			color: '--color-chart-money-value',
			detail: tariffCost
				? `Reported ${isEnergyLivePeriod(periodId) ? 'today' : 'period'} cost`
				: currency
					? `Cost in ${currency}`
					: 'Configure tariffs',
			helper: statistic?.tariffs?.length
				? `${statistic.tariffs.length} tariff intervals`
				: 'Set up electricity tariffs',
			statusLabel: equalRateSchedule ? 'Tariff schedule' : 'Off-peak',
			statusLabelKey: equalRateSchedule
				? 'energy_monitoring_tariff_schedule'
				: 'energy_monitoring_kpi_tariff_status',
			statusValue: offPeakLabel,
			progress: offPeakProgress,
			progressType: offPeakProgress >= 50 ? 'success' : 'warning',
			showStatus: Boolean(tariffCost || tariffSummary?.hasTariffs),
		},
		{
			id: 'currentTariff',
			icon: 'fas fa-receipt',
			value: currentTariff?.priceLabel ?? '—',
			infoText: 'Current tariff',
			color: '--color-chart-money-value',
			detail: currentTariff?.name ?? 'Tariff setup needed',
			helper: currentTariff ? 'Current electricity rate' : 'Set up an electricity tariff',
			statusLabel: 'Rate',
			statusValue: currentTariff?.priceLabel ?? '—',
			progress: 0,
			progressType: 'primary',
			showStatus: Boolean(currentTariff),
		},
		{
			id: 'offPeak',
			labelKey: equalRateSchedule
				? 'energy_monitoring_equal_rate_schedule'
				: 'energy_monitoring_kpi_offPeak',
			icon: 'fas fa-moon',
			value: offPeakLabel,
			infoText: equalRateSchedule ? 'Tariff schedule' : 'Off-peak usage',
			color: '--color-lightblue',
			detail: equalRateSchedule
				? 'Configured tariff bands use the same rate'
				: `${periodLabel} at the lowest rate`,
			helper: tariffSummary?.hasTariffs ? 'Based on configured tariff bands' : 'Configure tariffs',
			statusLabel: equalRateSchedule ? 'Rate comparison' : 'Off-peak share',
			statusLabelKey: equalRateSchedule
				? 'energy_monitoring_rate_comparison'
				: 'energy_monitoring_kpi_offPeak_status',
			statusValue: offPeakLabel,
			progress: offPeakProgress,
			progressType: offPeakProgress >= 50 ? 'success' : 'warning',
			showStatus: Boolean(tariffSummary?.hasTariffs),
		},
		{
			id: 'topConsumer',
			icon: 'fas fa-arrow-trend-up',
			value: topConsumer ? fmt(topConsumer.power) : '—',
			infoText: 'Top consumer now',
			color: '--color-warning',
			detail: topConsumer?.title ?? 'No activity to display',
			helper: 'Highest measured device load',
			statusLabel: 'Share of live load',
			statusValue:
				topConsumer && loadW > 0 ? `${Math.round((topConsumer.power / loadW) * 100)}%` : '—',
			progress:
				topConsumer && loadW > 0
					? Math.min(Math.round((topConsumer.power / loadW) * 100), 100)
					: 0,
			progressType: 'warning',
			showStatus: Boolean(topConsumer),
		},
		{
			id: 'standby',
			icon: 'fas fa-power-off',
			value: fmt(standbyW),
			infoText: 'Standby consumption',
			color: '--color-muted',
			detail: standbyW > 0 ? 'Low-power devices active now' : 'No standby load detected',
			helper: `Devices using up to ${STANDBY_POWER_THRESHOLD_W} W`,
			statusLabel: 'Share of live load',
			statusValue: loadW > 0 ? `${standbyProgress}%` : '—',
			progress: standbyProgress,
			progressType: standbyProgress > 15 ? 'danger' : standbyProgress > 0 ? 'warning' : 'primary',
			showStatus: standbyW > 0,
		},
		{
			id: 'peakDemand',
			icon: 'fas fa-chart-line',
			value: statistic?.max ?? '—',
			infoText: 'Peak consumption',
			color: '--color-danger',
			detail: `Highest ${periodLabel.toLowerCase()} interval`,
			helper: statistic?.max ? 'From consumption history' : 'No statistics available',
			statusLabel: 'Average',
			statusValue: statistic?.average ?? '—',
			progress: 0,
			progressType: 'danger',
			showStatus: Boolean(statistic?.max),
		},
	];
};

export type LivePowerPair = {
	loadLabel: string;
	loadValue: string;
	solarValue: string;
	selfPoweredPercent: number;
	gridLabel: string;
	progressType: 'success' | 'warning' | 'danger' | 'primary';
};

export type PeriodSummaryPair = {
	consumptionValue: string;
	costValue: string;
	periodLabel: string;
	tariffProgress: number;
	tariffProgressType: 'success' | 'warning' | 'danger' | 'primary';
	tariffCaption: string;
	tariffMode: 'none' | 'equal' | 'tiered';
};

export const buildLivePowerPair = (
	stores: ConsumptionStores,
	devices: Record<string, Device>,
	useDemoData = isEnergyMonitoringDemoMode(),
): LivePowerPair => {
	const kpis = buildEnergyKpis(stores, devices, '', null, 'live', null, useDemoData);
	const loadCard = kpis.find((card) => card.id === 'load')!;
	const solarCard = kpis.find((card) => card.id === 'solar')!;

	const selfPoweredPercent = solarCard.showStatus
		? Number.parseInt(solarCard.statusValue.replace('%', ''), 10) || 0
		: 0;

	return {
		loadLabel: loadCard.detail,
		loadValue: loadCard.value,
		solarValue: solarCard.value,
		selfPoweredPercent,
		gridLabel: loadCard.helper || solarCard.helper,
		progressType: loadCard.progressType,
	};
};

export const buildPeriodSummaryPair = (
	statistic: ChartStatisticInterface | null,
	tariffSummary: TariffSummary | null,
	periodId: EnergyMonitoringPeriodId,
	currency: string,
): PeriodSummaryPair => {
	void currency;
	const consumptionValue = statistic?.totalConsumed ?? '—';
	const costValue = tariffSummary?.totalCost ?? '—';
	const periodLabel = isEnergyLivePeriod(periodId) ? 'Today' : 'Period consumption';
	const tariffMode = !tariffSummary?.hasTariffs
		? 'none'
		: tariffSummary.hasDistinctRates
			? 'tiered'
			: 'equal';

	return {
		consumptionValue,
		costValue,
		periodLabel,
		tariffProgress: tariffSummary?.offPeakPercent ?? 0,
		tariffProgressType: (tariffSummary?.offPeakPercent ?? 0) >= 50 ? 'success' : 'warning',
		tariffCaption:
			tariffMode === 'equal'
				? 'All configured bands use the same price'
				: (tariffSummary?.offPeakLabel ?? '—'),
		tariffMode,
	};
};
